import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";

const ROLE_CONFIG = {
  directors: {
    label: "Directors",
    role: "director",
    codeKey: "directorCode",
    activateKey: "isDirectorActivated",
  },
  coordinators: {
    label: "Coordinators",
    role: "coordinator",
    codeKey: "coordinatorCode",
    activateKey: "isCoordinatorActivated",
  },
  influencers: {
    label: "Influencers",
    role: "influencer",
    codeKey: "influencerCode",
    activateKey: "isInfluencerActivated",
  },
};

const CFOPanelPDOnboardList = () => {
  const authState = useSelector((s) => s.authReducer || {});
  const [fetchData] = useFetch();

  const [activeRole, setActiveRole] = useState("directors");

  const [data, setData] = useState({
    directors: [],
    coordinators: [],
    influencers: [],
  });

  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");

  const role = ROLE_CONFIG[activeRole];
  const tabs = Object.keys(ROLE_CONFIG);
  const rows = data[activeRole] || [];

  const showMessage = (message) => {
    setStatusMsg(message);

    setTimeout(() => {
      setStatusMsg("");
    }, 3000);
  };

  const fetchRoleData = async (roleKey, forceRefresh = false) => {
    try {
      if (!forceRefresh && data[roleKey]?.length > 0) {
        return;
      }

      const config = ROLE_CONFIG[roleKey];

      setLoading(true);

      const res = await fetchData({
        url: "/cfo/pd/onboarding/role-list",
        method: "post",
        headers: {
          Authorization: authState?.token,
        },
        data: {
          role: config.role,
          think5whyscode:
            authState?.user?.think5whyscode,
        },
      });

      if (res?.success) {
        setData((prev) => ({
          ...prev,
          [roleKey]: res?.data || [],
        }));
      } else {
        showMessage("❌ Failed to fetch records");
      }
    } catch (error) {
      console.error(error);
      showMessage("❌ Server Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authState?.isLoggedIn) {
      fetchRoleData(activeRole);
    }
  }, [activeRole, authState?.isLoggedIn]);

  const updateRow = async (row) => {
    try {
      setSavingId(row._id);

      const config = ROLE_CONFIG[activeRole];

      const res = await fetchData({
        url: "/cfo/pd/onboarding/role-update",
        method: "post",
        headers: {
          Authorization: authState?.token,
        },
        data: {
          role: config.role,
          _id: row._id,
          payload: {
            isVerified: row.isVerified,
            isActive: row.isActive,
            [config.activateKey]:
              row[config.activateKey],
          },
        },
      });

      if (res?.success) {
        showMessage("✅ Updated Successfully");

        // Optional refresh from DB
        fetchRoleData(activeRole, true);
      } else {
        showMessage("❌ Update Failed");
      }
    } catch (error) {
      console.error(error);
      showMessage("❌ Server Error");
    } finally {
      setSavingId(null);
    }
  };

  const updateLocalRow = (
    id,
    field,
    value
  ) => {
    setData((prev) => ({
      ...prev,
      [activeRole]: prev[
        activeRole
      ].map((row) =>
        row._id === id
          ? {
              ...row,
              [field]: value,
            }
          : row
      ),
    }));
  };

  return (
    <div className="p-4 bg-gray-900 text-white rounded-xl space-y-4">

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-2">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() =>
              setActiveRole(tab)
            }
            disabled={loading}
            className={`py-2 rounded-lg text-xs font-medium transition-all ${
              activeRole === tab
                ? "bg-cyan-500 text-black"
                : "bg-gray-800 hover:bg-gray-700"
            }`}
          >
            {ROLE_CONFIG[tab].label}
          </button>
        ))}
      </div>

      {/* Status */}
      {statusMsg && (
        <div className="rounded-lg bg-gray-800 p-2 text-center text-xs text-green-400">
          {statusMsg}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="py-10 text-center text-gray-400">
          Loading records...
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-xs">
            <thead className="bg-gray-800">
              <tr>
                <th className="p-3 text-left">
                  Code
                </th>
                <th className="p-3">
                  Verified
                </th>
                <th className="p-3">
                  Active
                </th>
                <th className="p-3">
                  Activated
                </th>
                <th className="p-3">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr
                  key={row._id}
                  className="border-t border-gray-700 hover:bg-gray-800"
                >
                  <td className="p-3">
                    {
                      row[
                        role.codeKey
                      ]
                    }
                  </td>

                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={
                        row.isVerified ||
                        false
                      }
                      onChange={(e) =>
                        updateLocalRow(
                          row._id,
                          "isVerified",
                          e.target.checked
                        )
                      }
                    />
                  </td>

                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={
                        row.isActive ||
                        false
                      }
                      onChange={(e) =>
                        updateLocalRow(
                          row._id,
                          "isActive",
                          e.target.checked
                        )
                      }
                    />
                  </td>

                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={
                        row[
                          role
                            .activateKey
                        ] || false
                      }
                      onChange={(e) =>
                        updateLocalRow(
                          row._id,
                          role.activateKey,
                          e.target.checked
                        )
                      }
                    />
                  </td>

                  <td className="text-center">
                    <button
                      onClick={() =>
                        updateRow(row)
                      }
                      disabled={
                        savingId ===
                        row._id
                      }
                      className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600"
                    >
                      {savingId ===
                      row._id
                        ? "Saving..."
                        : "Save"}
                    </button>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="p-8 text-center text-gray-400"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CFOPanelPDOnboardList;